/**
 * Load Flow Dialog Component
 * Allows users to browse and load saved flows and templates
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Tabs,
  Tab,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  InputAdornment,
  CircularProgress
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import ArticleIcon from '@mui/icons-material/Article';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import type { SavedFlow, FlowTemplate } from '../types/flow';
import supabaseFlowStorageService from '../services/SupabaseFlowStorageService';
import { formatDistanceToNow } from 'date-fns';
import { confirmDialogService } from './ui/ConfirmDialog';
import TemplateGallery from './ui/TemplateGallery';

// Use Supabase storage for authenticated users
const flowStorageService = supabaseFlowStorageService;

interface LoadFlowDialogProps {
  open: boolean;
  onClose: () => void;
  onLoadFlow: (flow: SavedFlow) => void;
  onLoadTemplate: (template: FlowTemplate) => void;
  onRunTemplate?: (template: FlowTemplate) => void;
  currentFlowId?: string | null;
  onCurrentFlowDeleted?: () => void;
}

const LoadFlowDialog: React.FC<LoadFlowDialogProps> = ({
  open,
  onClose,
  onLoadFlow,
  onLoadTemplate,
  onRunTemplate,
  currentFlowId,
  onCurrentFlowDeleted
}) => {
  const [tabValue, setTabValue] = useState<'flows' | 'templates'>('flows');
  const [flows, setFlows] = useState<SavedFlow[]>([]);
  const [templates, setTemplates] = useState<FlowTemplate[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);

  // Load flows and templates
  useEffect(() => {
    if (open) {
      loadData();
    }
  }, [open]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [loadedFlows, loadedTemplates] = await Promise.all([
        flowStorageService.listFlows(),
        flowStorageService.listTemplates()
      ]);
      
      // Sort flows by updatedAt (newest first)
      const sortedFlows = loadedFlows.sort((a, b) => {
        const dateA = new Date(a.metadata.updatedAt).getTime();
        const dateB = new Date(b.metadata.updatedAt).getTime();
        return dateB - dateA; // Descending order (newest first)
      });
      
      // Sort templates by updatedAt (newest first)
      const sortedTemplates = loadedTemplates.sort((a, b) => {
        const dateA = new Date(a.metadata.updatedAt).getTime();
        const dateB = new Date(b.metadata.updatedAt).getTime();
        return dateB - dateA; // Descending order (newest first)
      });
      
      setFlows(sortedFlows);
      setTemplates(sortedTemplates);
    } catch (error) {
      console.error('Failed to load flows/templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string, isTemplate: boolean) => {
    const confirmed = await confirmDialogService.confirm({
      title: `Delete ${isTemplate ? 'Template' : 'Flow'}?`,
      message: `Are you sure you want to delete this ${isTemplate ? 'template' : 'flow'}? This action cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      type: 'danger'
    });
    
    if (!confirmed) {
      return;
    }

    try {
      if (isTemplate) {
        await flowStorageService.deleteTemplate(id);
      } else {
        await flowStorageService.deleteFlow(id);
        
        // If we just deleted the currently open flow, notify parent
        if (id === currentFlowId && onCurrentFlowDeleted) {
          onCurrentFlowDeleted();
        }
      }
      await loadData();
    } catch (error) {
      console.error('Failed to delete:', error);
    }
  };

  // `listFlows` returns dashboard summaries — metadata only, with an empty
  // `state`. Handing one straight to the editor would open a real knod as a
  // blank canvas, and the next auto-save would write that blankness back over
  // its graph. Fetch the flow before the editor ever sees it.
  const handleLoad = async (item: SavedFlow | FlowTemplate) => {
    if (item.isTemplate) {
      onLoadTemplate(item as FlowTemplate);
      onClose();
      return;
    }

    const full = await flowStorageService.loadFlow(item.metadata.id);
    if (!full) {
      console.error('Failed to open knod: flow not found', item.metadata.id);
      return;
    }
    onLoadFlow(full);
    onClose();
  };

  const handleTemplateLoad = (template: FlowTemplate) => {
    onLoadTemplate(template);
    onClose();
  };

  const handleTemplateRun = (template: FlowTemplate) => {
    if (onRunTemplate) {
      onRunTemplate(template);
      onClose();
    }
  };

  const handleTemplateDelete = async (template: FlowTemplate) => {
    const confirmed = await confirmDialogService.confirm({
      title: 'Delete Template?',
      message: `Are you sure you want to delete "${template.metadata.name}"? This action cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      type: 'danger'
    });
    
    if (!confirmed) return;

    try {
      await flowStorageService.deleteTemplate(template.metadata.id);
      await loadData();
    } catch (error) {
      console.error('Failed to delete template:', error);
    }
  };

  const filterItems = (items: (SavedFlow | FlowTemplate)[]) => {
    if (!searchQuery) return items;
    
    const query = searchQuery.toLowerCase();
    return items.filter(item =>
      item.metadata.name.toLowerCase().includes(query) ||
      item.metadata.description?.toLowerCase().includes(query) ||
      item.metadata.tags?.some(tag => tag.toLowerCase().includes(query))
    );
  };

  const filteredFlows = filterItems(flows);
  // @ts-expect-error TS6133 - Kept for future use when template tab is enabled
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _filteredTemplates = filterItems(templates);

  const renderList = (items: (SavedFlow | FlowTemplate)[], isTemplate: boolean) => {
    if (loading) {
      return (
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" p={4} gap={2}>
          <CircularProgress size={32} sx={{ color: 'var(--color-accent-primary)' }} />
          <Typography sx={{ color: 'var(--color-text-secondary)' }}>
            Loading {isTemplate ? 'templates' : 'flows'}...
          </Typography>
        </Box>
      );
    }

    if (items.length === 0) {
      return (
        <Box display="flex" flexDirection="column" alignItems="center" p={3}>
          <Typography color="textSecondary">
            {searchQuery ? 'No results found' : `No ${isTemplate ? 'templates' : 'flows'} saved yet`}
          </Typography>
        </Box>
      );
    }

    return (
      <List>
        {items.map((item) => (
          <ListItem
            key={item.metadata.id}
            component="div"
            onClick={() => { void handleLoad(item); }}
            style={{
              borderBottom: '1px solid var(--node-border-color)',
              backgroundColor: 'var(--node-header-background)',
              marginBottom: '4px',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            <ListItemText
              primary={
                <Box display="flex" alignItems="center" gap={1}>
                  {isTemplate ? <ArticleIcon fontSize="small" /> : <FolderIcon fontSize="small" />}
                  <Typography style={{ color: 'var(--color-text-primary)', fontWeight: 500 }}>
                    {item.metadata.name}
                  </Typography>
                </Box>
              }
              secondary={
                <Box component="span" display="block" mt={0.5}>
                  {item.metadata.description && (
                    <Typography
                      variant="body2"
                      component="span"
                      display="block"
                      style={{ color: 'var(--color-text-secondary)', marginBottom: '4px' }}
                    >
                      {item.metadata.description}
                    </Typography>
                  )}
                  <Box component="span" display="flex" alignItems="center" gap={1} flexWrap="wrap">
                    <Typography variant="caption" component="span" style={{ color: 'var(--color-text-secondary)' }}>
                      Updated {formatDistanceToNow(new Date(item.metadata.updatedAt), { addSuffix: true })}
                    </Typography>
                    {item.metadata.tags?.map((tag) => (
                      <Chip
                        key={tag}
                        label={tag}
                        size="small"
                        style={{
                          backgroundColor: 'var(--color-accent-primary)',
                          color: 'white',
                          height: '20px',
                          fontSize: '10px'
                        }}
                      />
                    ))}
                    {isTemplate && (item as FlowTemplate).category && (
                      <Chip
                        label={(item as FlowTemplate).category}
                        size="small"
                        style={{
                          backgroundColor: 'var(--color-accent-secondary)',
                          color: 'white',
                          height: '20px',
                          fontSize: '10px'
                        }}
                      />
                    )}
                    {isTemplate && (item as FlowTemplate).usageCount !== undefined && (
                      <Typography variant="caption" component="span" style={{ color: 'var(--color-text-secondary)' }}>
                        Used {(item as FlowTemplate).usageCount} times
                      </Typography>
                    )}
                  </Box>
                </Box>
              }
              secondaryTypographyProps={{ component: 'div' }}
            />
            <ListItemSecondaryAction>
              <IconButton
                edge="end"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete(item.metadata.id, isTemplate);
                }}
                style={{ color: 'var(--color-accent-error)' }}
              >
                <DeleteIcon />
              </IconButton>
            </ListItemSecondaryAction>
          </ListItem>
        ))}
      </List>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        style: {
          backgroundColor: 'var(--node-body-background)',
          color: 'var(--color-text-primary)',
          border: '1px solid var(--node-border-color)',
          maxHeight: '85vh'
        }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">Load Knod or Template</Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Tabs */}
        <Tabs
          value={tabValue}
          onChange={(_, newValue) => setTabValue(newValue)}
          style={{ borderBottom: '1px solid var(--node-border-color)', marginBottom: '16px' }}
        >
          <Tab
            label={`My Knods (${flows.length})`}
            value="flows"
            style={{ color: 'var(--color-text-primary)' }}
          />
          <Tab
            label={`Templates (${templates.length})`}
            value="templates"
            style={{ color: 'var(--color-text-primary)' }}
          />
        </Tabs>

        {/* Content */}
        <Box style={{ maxHeight: '500px', overflowY: 'auto' }}>
          {tabValue === 'flows' && (
            <>
              {/* Search for flows */}
              <TextField
                fullWidth
                placeholder="Search by name, description, or tags..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                variant="outlined"
                size="small"
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon style={{ color: 'var(--color-text-secondary)' }} />
                    </InputAdornment>
                  ),
                  style: {
                    color: 'var(--color-text-primary)',
                    backgroundColor: 'var(--node-header-background)',
                    borderColor: 'var(--node-border-color)'
                  }
                }}
              />
              {renderList(filteredFlows, false)}
            </>
          )}
          {tabValue === 'templates' && (
            <TemplateGallery
              templates={templates}
              loading={loading}
              onLoadTemplate={handleTemplateLoad}
              onRunTemplate={onRunTemplate ? handleTemplateRun : undefined}
              onDeleteTemplate={handleTemplateDelete}
              emptyMessage="No templates available yet"
            />
          )}
        </Box>
      </DialogContent>

      <DialogActions style={{ padding: '16px 24px' }}>
        <Button
          onClick={onClose}
          style={{ color: 'var(--color-text-secondary)' }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LoadFlowDialog;
